// composables/useDownloadArquivo.js
import { ref } from 'vue';
import axios from 'axios';

export function useDownloadArquivo() {
    
    const baixandoArquivo = ref(false);
    const progressoDownload = ref(0);
    
    // Monta endereço da api
    const API_BASE_URL = import.meta.env.VITE_API_URL  

    /**
     * Download de arquivo existente
     * @param {Object} fileObj - Objeto do arquivo (deve conter uniqueId)
     * @param {string} tabela - Nome da tabela onde o arquivo está registrado
     * @param {Object} options - Opções adicionais
     * @returns {Promise<boolean>} - Sucesso ou falha
     */
    
    const downloadArquivo = async (fileObj, tabela, options = {}) => {
    
        const {
            showProgress = false,
            onProgress = null,
            onError = null,
            onSuccess = null
        } = options;

        // Verifica se o arquivo possui uniqueId
        if (!fileObj?.uniqueId) {
            const mensagem = 'Arquivo ainda não foi salvo no servidor';
            if (onError) onError(mensagem);
            throw new Error(mensagem);
        }

        // Verifica se a tabela foi informada
        if (!tabela) {
            const mensagem = 'Tabela não informada para download';
            if (onError) onError(mensagem);
            throw new Error(mensagem);
        }

        try {

            baixandoArquivo.value = true;
            
            if (showProgress) progressoDownload.value = 0;

            const url = `${API_BASE_URL}/download/${fileObj.uniqueId}/${tabela}`;

            const response = await axios({
                method: 'GET',
                url: url,
                responseType: 'blob',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                onDownloadProgress: (progressEvent) => {
                    if (showProgress && progressEvent.total) {
                        const percentual = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                        progressoDownload.value = percentual;
                        if (onProgress) onProgress(percentual);
                    }
                }
            });

            // Verifica se o blob é válido
            if (!response.data || response.data.size === 0) {
                throw new Error('Arquivo vazio ou corrompido');
            }

            // Cria o download
            const blobUrl = window.URL.createObjectURL(response.data);
            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = fileObj.originalName || fileObj.filename || 'download';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(blobUrl);

            if (onSuccess) onSuccess();
            
            return true;

        } catch (erro) {
            console.error('Erro no download:', erro);
            
            let mensagemErro = 'Erro ao baixar arquivo';
            
            if (erro.response && erro.response.data instanceof Blob) {
                try {
                    const errorText = await erro.response.data.text();
                    const errorJson = JSON.parse(errorText);
                    mensagemErro = errorJson.mensagem || mensagemErro;
                } catch (e) {
                    mensagemErro = erro.message || mensagemErro;
                }
            } else if (erro.response?.data?.mensagem) {
                mensagemErro = erro.response.data.mensagem;
            } else if (erro.message) {
                mensagemErro = erro.message;
            }
            
            if (onError) onError(mensagemErro);
            throw new Error(mensagemErro);
            
        } finally {
            baixandoArquivo.value = false;
            if (showProgress) {
                setTimeout(() => {
                    progressoDownload.value = 0;
                }, 1000);
            }
        }
    };

    return {
        downloadArquivo,
        baixandoArquivo,
        progressoDownload
    };
}